import { create } from 'zustand';

interface AudioState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  activeMessageId: string | null;
  volume: number;
  error: string | null;

  play: (url: string, messageId: string) => Promise<void>;
  togglePlayPause: (messageId: string) => void;
  stop: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
}

const audio = new Audio();
let listenersAttached = false;

function attachListeners(set: (partial: Partial<AudioState>) => void) {
  if (listenersAttached) return;
  listenersAttached = true;

  audio.addEventListener('timeupdate', () => {
    set({ currentTime: audio.currentTime });
  });
  audio.addEventListener('loadedmetadata', () => {
    set({ duration: audio.duration });
  });
  audio.addEventListener('ended', () => {
    set({ isPlaying: false, currentTime: 0 });
  });
  audio.addEventListener('error', () => {
    set({ error: 'Playback error', isPlaying: false });
  });
}

/**
 * Singleton audio playback store backed by a module-level `<audio>` element.
 * Tracks the currently playing message and exposes play/pause/stop/seek.
 * Revokes the previous blob URL when starting a new one to avoid leaks.
 */
export const useAudioStore = create<AudioState>((set, get) => {
  attachListeners(set);

  return {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    activeMessageId: null,
    volume: 1,
    error: null,

    play: async (url: string, messageId: string) => {
      const currentId = get().activeMessageId;

      if (currentId === messageId && audio.src) {
        try {
          await audio.play();
          set({ isPlaying: true });
        } catch (e) {
          set({ error: (e as Error).message, isPlaying: false });
        }
        return;
      }

      // Revoke previous blob URL to prevent memory leak
      if (audio.src && audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }

      audio.src = url;
      audio.load();
      set({ activeMessageId: messageId, currentTime: 0, error: null });

      try {
        await audio.play();
        set({ isPlaying: true });
      } catch (e) {
        set({ error: (e as Error).message, isPlaying: false });
      }
    },

    togglePlayPause: (messageId: string) => {
      const { activeMessageId, isPlaying } = get();

      if (activeMessageId !== messageId) return;

      if (isPlaying) {
        audio.pause();
        set({ isPlaying: false });
      } else {
        audio
          .play()
          .then(() => set({ isPlaying: true }))
          .catch((e) => set({ error: (e as Error).message, isPlaying: false }));
      }
    },

    stop: () => {
      audio.pause();
      audio.currentTime = 0;
      set({ isPlaying: false, activeMessageId: null, currentTime: 0 });
    },

    seek: (time: number) => {
      if (Number.isFinite(time)) {
        audio.currentTime = time;
        set({ currentTime: time });
      }
    },

    setVolume: (volume: number) => {
      audio.volume = Math.max(0, Math.min(1, volume));
      set({ volume });
    },
  };
});
